import React from 'react';
import { HashRouter, Route } from 'react-router-dom';
import { parse } from 'query-string';

import { ConfigCentralContainer } from './components/config_central/config_central_container';
import { DashboardContainer } from './components/contra_cogs/dashboardcontainer';
import { createJSClient } from './components/js_client';
import { Loading } from './components/loading';

import { Main } from './components/main';
import { Mapper } from './components/mapper/mapper';
import NavigationMenu from './components/navigation/navigation_menu';
import { SimulatorPage } from './components/simulator/simulator_page';

import './styles/header.css';
import './styles/simulator.css';
import SpotAccuracy from './components/spotcp/accuracy';
import AccessRestrictedComponent from './components/access_restricted_component';
import { isPowerUser, isPublic } from './utils/access-utils';
import logger from './utils/logging';
import { Spotlight } from './components/spotlight/spotlight_main';
import { newFeatureItemsToDisplay } from './components/feature_tour/utils.jsx';

const DashboardContainerWithAlert = AccessRestrictedComponent(DashboardContainer);
const MapperWithAlert = AccessRestrictedComponent(Mapper);
const SimulatorPageWithAlert = AccessRestrictedComponent(SimulatorPage);

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      displayNewFeatureItems: [],
      trackedEvents: [],
      queryParameters: parse(location.search)
    };    
  }

  componentWillMount = () => {
    this.client = createJSClient(this.initialClientTasks);
  };

  initialClientTasks = () => {
    this.setState({readOnly: true});
    this.client.getIdentity((result) => {
      const identity = {...result, powerUser: isPowerUser(result)};
      this.setState({readOnly: isPublic(identity), identity: identity}, () => {
        logger.updateLogger(this.state.identity);
        this.getFeatureAccessConfig();
        this.setAppConfig();
        this.getNewFeatureDisplayItems();
      });
    });
  };

  setAppConfig = () => {
    this.client.getAppConfig((result) => {
      this.setState({appConfig: result.config});
    });
  };

  checkAccess = (feature, identity) => {
    if (!identity) {
      return false;
    }
    return (feature.beta && identity.betaFeatures) ||
      (feature.accessLevels && feature.accessLevels.includes(identity.access)) ||
      (feature.permittedUsers && feature.permittedUsers.includes(identity.username));
  };

  getFeatureAccessConfig = () => {
    this.client.getFeatureAccessItems(
      (result) => {
        const featureAccessConfigs = result.featureAccessList;
        const featureList = featureAccessConfigs.filter(f => this.checkAccess(f, this.state.identity))
                                                .map(f => f.featureId);
        if (this.state.identity) {
          console.log(`${ this.state.identity.username } has access to: ${ [featureList] }`);
        } else {
          console.log(`No local cache for identity, trying to fetch.`);
        }
        this.setState({featureList: featureList});
      },
      (error) => console.log(error),
    );
  };

  getNewFeatureDisplayItems = () => {
    this.client.getNewFeatureDisplayItems(
      (result) => {
        const items = newFeatureItemsToDisplay(result.newFeatureDisplayEntries);
        this.setState({displayNewFeatureItems: items});
      },
      (error) => console.log(error),
    );
  };

  recordTrackedEvent = ({loadedReportType}) => {
    if (loadedReportType) {
      this.setState({trackedEvents: [...this.state.trackedEvents, `reportLoaded${ loadedReportType }`]});
    } else {
      this.setState({trackedEvents: this.state.trackedEvents.filter((event) => !event.startsWith('reportLoaded'))});
    }
  };

  render() {
    return (
      <div className="awsui awsui-overwrite">
        { ' ' }
        {/* eslint-disable-line react/jsx-filename-extension */ }
        <HashRouter>
          <div id="b">
            { !(this.state.identity && this.state.appConfig) && <Route path="/" component={ Loading } /> }
            <NavigationMenu
              readOnly={ this.state.readOnly }
              identity={ this.state.identity }
              featureList={ this.state.featureList }
              displayNewFeatureItems={ this.state.displayNewFeatureItems }
              trackedEvents={ this.state.trackedEvents }
            />
            { this.state.identity && this.state.appConfig && (
              <div>
                <Route
                  exact
                  path="/"
                  render={ (props) => (
                    <Main
                      {...this.state}
                      { ...props }
                      recordTrackedEvent={ this.recordTrackedEvent }
                    />
                  ) }
                />
                <Route
                  path="/reports/:id"
                  render={ (props) => (
                    <Main
                      identity={ this.state.identity }
                      appConfig={ this.state.appConfig }
                      featureList={ this.state.featureList }
                      { ...props }
                      recordTrackedEvent={ this.recordTrackedEvent }
                    />
                  ) }
                />
                <Route
                  path="/accuracy-mappings"
                  render={ (props) => (
                    <MapperWithAlert
                      identity={ this.state.identity }
                      appConfig={ this.state.appConfig }
                      featureList={ this.state.featureList }
                      appId={ 'mapper' }
                      alertMessage={ 'You don\'t have access to Accuracy Mappings.' }
                      { ...props }
                    />
                  ) }
                />
                <Route
                  path="/dashboard"
                  render={ (props) => (
                    <DashboardContainerWithAlert
                      identity={ this.state.identity }
                      appConfig={ this.state.appConfig }
                      featureList={ this.state.featureList }
                      appId={ 'dashboard' }
                      alertMessage={ 'You don\'t have access to Contra Cogs Bridging.' }
                      { ...props }
                    />
                  ) }
                />
                <Route
                  exact
                  path="/spotlight"
                  render={ (props) => <Spotlight identity={ this.state.identity } featureList={ this.state.featureList } { ...props } /> }
                />
                <Route
                  path="/spotlight/:type&:marketplaceId&:merchantId&:asin"
                  render={ (props) => <Spotlight identity={ this.state.identity } featureList={ this.state.featureList } { ...props } /> }
                />
                <Route
                  exact
                  path="/spotcp-accuracy"
                  render={(props) => <SpotAccuracy identity={this.state.identity} featureList={this.state.featureList} {...props} />}
                />
                <Route
                  path="/config-central"
                  render={ (props) => (
                    <ConfigCentralContainer
                      identity={ this.state.identity }
                      appConfig={ this.state.appConfig }
                      setAppConfig={ this.setAppConfig }
                      featureList={ this.state.featureList }
                      { ...props }
                    />
                  ) }
                />
                <Route
                  path="/simulator"
                  render={ (props) => (
                    <SimulatorPageWithAlert
                      identity={ this.state.identity }
                      appConfig={ this.state.appConfig }
                      featureList={ this.state.featureList }
                      appId={ 'simulator' }
                      alertMessage={ 'You don\'t have access to Simulator.' }
                      { ...props }
                    />
                  ) }
                />
              </div>
            ) }
          </div>
        </HashRouter>
      </div>
    );
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js